import React, { useState, useEffect } from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	TabPane,
	TabContent,
	FormFeedback,
	FormGroup,
	ModalFooter,
} from 'reactstrap';

// Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

//redux
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { Select } from '@mantine/core';
import { normalizeFlowRatePayload } from '../../../utils/normalizeFlowRatePayload';
import {
	getRightExerciseTypeList,
	getFlowRateUnitList,
	getWaterSourceList
} from '../../../slices/thunks';

const emptyToUndefined = (_v, orig) => (orig === '' || orig == null ? undefined : orig);
const monthField = () => Yup.number()
	.transform(emptyToUndefined)
	.nullable()
	.notRequired()
	.min(0, 'Debe ser mayor o igual a 0');

const ListFlowRatesForm = ({
	dataParticipant,
	isEdit,
	handleToggleModal,
	isOpen,
	handleSave,
	selectedWaterSourceIds = []
}) => {
	const dispatch = useDispatch();

	const [uniformMonthly, setUniformMonthly] = useState(false);

	// Selectores para los datos maestros
	const selectorRightExerciseType = createSelector(
		state => state.RightExerciseType,
		data => ({
			statementListSelect: data.statementListSelect || [],
			isLoadingList: data.isLoadingList
		})
	);
	const { statementListSelect: rightExerciseTypeList } = useSelector(selectorRightExerciseType);

	const selectorFlowRateUnit = createSelector(
		state => state.FlowRateUnit,
		data => ({
			statementListSelect: data.statementListSelect || [],
			isLoadingList: data.isLoadingList
		})
	);
	const { statementListSelect: flowRateUnitList } = useSelector(selectorFlowRateUnit);

	const selectorWaterSource = createSelector(
		state => state.WaterSource,
		data => ({
			statementListSelect: data.statementListSelect || [],
			isLoadingList: data.isLoadingList
		})
	);
	const { statementListSelect: waterSourceList } = useSelector(selectorWaterSource);

	useEffect(() => {
		dispatch(getRightExerciseTypeList());
		dispatch(getFlowRateUnitList());
		dispatch(getWaterSourceList());
	}, [dispatch]);

	// validation
	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			right_exercise_type_id: dataParticipant?.right_exercise_type?._id || dataParticipant?.right_exercise_type_id || '',
			flow_rate_unit_id: dataParticipant?.flow_rate_unit?._id || dataParticipant?.flow_rate_unit_id || '',
			water_source_id: dataParticipant?.water_source?._id || dataParticipant?.water_source_id || '',
			january: dataParticipant?.january ?? '',
			february: dataParticipant?.february ?? '',
			march: dataParticipant?.march ?? '',
			april: dataParticipant?.april ?? '',
			may: dataParticipant?.may ?? '',
			june: dataParticipant?.june ?? '',
			july: dataParticipant?.july ?? '',
			august: dataParticipant?.august ?? '',
			september: dataParticipant?.september ?? '',
			october: dataParticipant?.october ?? '',
			november: dataParticipant?.november ?? '',
			december: dataParticipant?.december ?? '',
		},
		validationSchema: Yup.object({
			right_exercise_type_id: Yup.string().required('Campo requerido'),
			flow_rate_unit_id: Yup.string().required('Campo requerido'),
			water_source_id: Yup.string().required('Campo requerido'),
			january: monthField(),
			february: monthField(),
			march: monthField(),
			april: monthField(),
			may: monthField(),
			june: monthField(),
			july: monthField(),
			august: monthField(),
			september: monthField(),
			october: monthField(),
			november: monthField(),
			december: monthField(),
		}),
		onSubmit: async (values) => {
			handleSave(normalizeFlowRatePayload(values));
			validation.resetForm();
		},
	});

	// Handle uniform monthly value
	useEffect(() => {
		if (uniformMonthly && validation.values.january !== '' && validation.values.january != null) {
			const value = validation.values.january;
			['february', 'march', 'april', 'may', 'june', 'july', 
			 'august', 'september', 'october', 'november', 'december'].forEach(month => {
				validation.setFieldValue(month, value);
			});
		}
	}, [uniformMonthly, validation.values.january]);

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<TabContent activeTab={'1'}>
					<TabPane tabId='1'>
						<Row className='mb-3'>
							<Col>
								<div className="form-check form-switch form-switch-md">
								<Input 
									className="form-check-input" 
									type="checkbox" 
									role="switch" 
									id="value_uniform_monthly"
									checked={uniformMonthly}
									onChange={(e) => setUniformMonthly(e.target.checked)}
								/>
									<Label className="form-check-label" for="value_uniform_monthly">¿Valor Uniforme Mensual?</Label>
								</div>
							</Col>
						</Row>
						<Row className='g-3 mb-3'>
							<Col className='mb-3'>
								<Label for='january' className='form-label'>
									Enero
								</Label>
								<Input
									name='january'
									id='january'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.january ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.january && validation.errors.january}
								/>
								{validation.touched.january && validation.errors.january && (
									<FormFeedback type="invalid">{validation.errors.january}</FormFeedback>
								)}
							</Col>							
							<Col className='mb-3'>
								<Label for='february' className='form-label'>
									Febrero
								</Label>
								<Input
									name='february'
									id='february'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.february ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.february && validation.errors.february}
								/>
								{validation.touched.february && validation.errors.february && (
									<FormFeedback type="invalid">{validation.errors.february}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='march' className='form-label'>
									Marzo
								</Label>
								<Input
									name='march'
									id='march'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.march ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.march && validation.errors.march}
								/>
								{validation.touched.march && validation.errors.march && (
									<FormFeedback type="invalid">{validation.errors.march}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='april' className='form-label'>
									Abril
								</Label>
								<Input
									name='april'
									id='april'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.april ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.april && validation.errors.april}
								/>
								{validation.touched.april && validation.errors.april && (
									<FormFeedback type="invalid">{validation.errors.april}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='may' className='form-label'>
									Mayo
								</Label>
								<Input
									name='may'
									id='may'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.may ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.may && validation.errors.may}
								/>
								{validation.touched.may && validation.errors.may && (
									<FormFeedback type="invalid">{validation.errors.may}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='june' className='form-label'>
									Junio
								</Label>
								<Input
									name='june'
									id='june'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.june ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.june && validation.errors.june}
								/>
								{validation.touched.june && validation.errors.june && (
									<FormFeedback type="invalid">{validation.errors.june}</FormFeedback>
								)}
							</Col>
						</Row>
						<Row className='g-3 mb-3'>
							<Col className='mb-3'>
								<Label for='july' className='form-label'>
									Julio
								</Label>
								<Input
									name='july'
									id='july'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.july ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.july && validation.errors.july}
								/>
								{validation.touched.july && validation.errors.july && (
									<FormFeedback type="invalid">{validation.errors.july}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='august' className='form-label'>
									Agosto
								</Label>
								<Input
									name='august'
									id='august'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.august ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.august && validation.errors.august}
								/>
								{validation.touched.august && validation.errors.august && (
									<FormFeedback type="invalid">{validation.errors.august}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='september' className='form-label'>
									Septiembre
								</Label>
								<Input
									name='september'
									id='september'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.september ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.september && validation.errors.september}
								/>
								{validation.touched.september && validation.errors.september && (
									<FormFeedback type="invalid">{validation.errors.september}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='october' className='form-label'>
									Octubre
								</Label>
								<Input
									name='october'
									id='october'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.october ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.october && validation.errors.october}
								/>
								{validation.touched.october && validation.errors.october && (
									<FormFeedback type="invalid">{validation.errors.october}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='november' className='form-label'>
									Noviembre
								</Label>
								<Input
									name='november'
									id='november'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.november ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.november && validation.errors.november}
								/>
								{validation.touched.november && validation.errors.november && (
									<FormFeedback type="invalid">{validation.errors.november}</FormFeedback>
								)}
							</Col>
							<Col className='mb-3'>
								<Label for='december' className='form-label'>
									Diciembre
								</Label>
								<Input
									name='december'
									id='december'
									className='form-control'
									type='number'
									step='any'
									min='0'
									style={{maxWidth: '200px'}}
									value={validation.values.december ?? ''}
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									invalid={validation.touched.december && validation.errors.december}
								/>
								{validation.touched.december && validation.errors.december && (
									<FormFeedback type="invalid">{validation.errors.december}</FormFeedback>
								)}
							</Col>
						</Row>
						<Row className='g-3'>
							<Col className='mb-3'>
								<Select
									label="Ejercicio del Derecho"
									id="right_exercise_type_id"
									name="right_exercise_type_id"
									value={validation.values.right_exercise_type_id || null}
									data={rightExerciseTypeList}
									onChange={(val) => validation.setFieldValue('right_exercise_type_id', val)}
									onBlur={() => validation.setFieldTouched('right_exercise_type_id', true)}
									error={validation.touched.right_exercise_type_id && validation.errors.right_exercise_type_id}
									placeholder="Seleccione Ejercicio del Derecho"
									searchable
									clearable
									style={{ width: '100%' }}
								/>
							</Col>
							<Col className='mb-3'>
								<Select
									label="Unidad de Agua Autorizada"
									id="flow_rate_unit_id"
									name="flow_rate_unit_id"
									value={validation.values.flow_rate_unit_id || null}
									data={flowRateUnitList}
									onChange={(val) => validation.setFieldValue('flow_rate_unit_id', val)}
									onBlur={() => validation.setFieldTouched('flow_rate_unit_id', true)}
									error={validation.touched.flow_rate_unit_id && validation.errors.flow_rate_unit_id}
									placeholder="Seleccione Unidad de Agua Autorizada"
									searchable
									clearable
									style={{ width: '100%' }}
								/>
							</Col>
							<Col className='mb-3'>
								<Select
									label="Fuente"
									id="water_source_id"
									name="water_source_id"
									value={validation.values.water_source_id || null}
									data={waterSourceList.filter(item => selectedWaterSourceIds.includes(item.value))}
									onChange={(val) => validation.setFieldValue('water_source_id', val)}
									onBlur={() => validation.setFieldTouched('water_source_id', true)}
									error={validation.touched.water_source_id && validation.errors.water_source_id}
									placeholder="Seleccione Fuente"
									searchable
									clearable
									style={{ width: '100%' }}
								/>
							</Col>
						</Row>
					</TabPane>
				</TabContent>
			</ModalBody>
			<ModalFooter>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}} className='btn-danger btn-label'>
						<i className="ri-close-line label-icon"></i>
						Cancelar
					</Button>
					<button
						type='button'
						className='btn btn-label btn-primary'
						onClick={() => {
							validation.handleSubmit();
						}}
					>
						<i className='ri-save-line label-icon'></i>
						Guardar
					</button>
				</div>
			</ModalFooter>
		</Form>
	);
};

export default ListFlowRatesForm;
